import { Container, Tabs, Alert, Text } from "@mantine/core";
import {
  IconMail,
  IconPackage,
  IconSearch,
  IconSettings,
  IconRobot,
  IconWorldWww,
} from "@tabler/icons-react";
import { useSearch, useLocation } from "wouter";
import { useBrandContext } from "../Brands/brandContext";
import { useBrand } from "../Brands/useBrand";
import BrandKitEditor from "../BrandKit/BrandKitEditor";
import BrandSetupWizard from "../Brands/BrandSetupWizard";
import BrandDetailsSetup from "../Brands/BrandDetailsSetup";
import WeeklyReportSettings from "./WeeklyReportSettings";
import GscSettings from "./GscSettings";
import AutopilotSettings from "./AutopilotSettings";
import SiteRefreshSettings from "./SiteRefreshSettings";
import { useCanUseContent } from "../_Shared/useCanUseContent";

export default function SettingsPage() {
  const { selectedBrand } = useBrandContext();
  const brand = useBrand(selectedBrand?.id);
  const [, setLocation] = useLocation();
  const canUseContent = useCanUseContent();

  const searchString = useSearch();
  const tabFromUrl = new URLSearchParams(searchString).get("tab");

  if (!selectedBrand) {
    return (
      <Container>
        <Alert color="blue" mt="md">
          Please select a brand from the switcher above to view settings.
        </Alert>
      </Container>
    );
  }

  if (!brand) {
    return null;
  }

  // A brand that isn't Active yet lands on Setup. MonitorSetup used to fall through to the
  // Brand Kit tab, so anyone following a "finish your setup" prompt arrived at the wrong page
  // and had to find the wizard themselves.
  const defaultTab = brand.setupStatus === "Active" ? "kit" : "setup";

  // Any ?tab= this page does not own falls back, rather than setting a Tabs value with no matching
  // panel — which renders the tab strip above an empty body and reads as a broken page.
  //
  // This was previously special-cased for autopilot alone, the one instance someone noticed. The
  // general form matters because tabs move between pages: "prompts" lived here until it moved to
  // /llm-config, and every link, bookmark and old email pointing at /settings?tab=prompts has been
  // rendering blank ever since. A tab leaving this page must not be able to do that again.
  // Mirrors the conditions on the Tabs.Tab / Tabs.Panel pairs below. A tab that is hidden for this
  // brand or this role is as dead as one that does not exist.
  const knownTabs = ["kit", "search-console", "setup"];
  if (brand.setupStatus === "MonitorSetup" || brand.setupStatus === "Active")
    knownTabs.push("reports");
  if (canUseContent) knownTabs.push("autopilot", "site");

  const activeTab = knownTabs.includes(tabFromUrl) ? tabFromUrl : defaultTab;

  return (
    <Container>
      <Tabs keepMounted={false} value={activeTab} onChange={(tab) => setLocation(`/settings?tab=${tab}`)}>
        <Tabs.List>
          <Tabs.Tab
            value="kit"
            leftSection={<IconPackage size={16} stroke={1.5} />}
          >
            Brand Kit
          </Tabs.Tab>
          {(brand.setupStatus === "MonitorSetup" || brand.setupStatus === "Active") && (
            <Tabs.Tab
              value="reports"
              leftSection={<IconMail size={16} stroke={1.5} />}
            >
              Email Reports
            </Tabs.Tab>
          )}
          <Tabs.Tab
            value="search-console"
            leftSection={<IconSearch size={16} stroke={1.5} />}
          >
            Search Console
          </Tabs.Tab>
          {canUseContent && (
            <Tabs.Tab
              value="autopilot"
              leftSection={<IconRobot size={16} stroke={1.5} />}
            >
              Autopilot
            </Tabs.Tab>
          )}
          {canUseContent && (
            <Tabs.Tab
              value="site"
              leftSection={<IconWorldWww size={16} stroke={1.5} />}
            >
              Site
            </Tabs.Tab>
          )}
          <Tabs.Tab
            value="setup"
            leftSection={<IconSettings size={16} stroke={1.5} />}
          >
            Setup
          </Tabs.Tab>
        </Tabs.List>

        {activeTab === "kit" && (
          <Text size="sm" c="dimmed" mt="sm">
            What every article must say: positioning, voice, glossary, facts, visual style.
            Setup is how you start monitoring.
          </Text>
        )}
        {activeTab === "setup" && (
          <Text size="sm" c="dimmed" mt="sm">
            Get this brand to Active: identity, site, industries, competitors, and what we ask AI.
            Writing voice lives in Brand Kit.
          </Text>
        )}

        <Tabs.Panel value="kit">
          <BrandKitEditor brand={brand} />
        </Tabs.Panel>
        <Tabs.Panel value="reports">
          <WeeklyReportSettings brand={brand} />
        </Tabs.Panel>
        <Tabs.Panel value="search-console">
          <GscSettings brand={brand} />
        </Tabs.Panel>
        {canUseContent && (
          <Tabs.Panel value="autopilot">
            <AutopilotSettings brand={brand} />
          </Tabs.Panel>
        )}
        {canUseContent && (
          <Tabs.Panel value="site">
            <SiteRefreshSettings key={brand.id} brand={brand} />
          </Tabs.Panel>
        )}
        <Tabs.Panel value="setup">
          {brand.setupStatus === "Active"
            ? <BrandDetailsSetup brand={brand} />
            : <BrandSetupWizard brand={brand} />}
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}
